import React, { useState } from "react";

import { IEvent, IShow, ShowDto, AkcAgilitySctDto, AkcAgilitySctEntryDto } from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";

import { toast } from "src/components/Toast/ToastManager";
import TabContent, { ITab, useTabContent } from "src/components/TabContent/TabContent";
import Button from "src/components/Button/Button";
import { SctTable } from "./SCTTable";

import styles from "./AKCAgilitySCTTab.module.scss";

interface IProps {
  event: IEvent
  show: IShow
}

const AKCAgilitySCTTab: React.FC<IProps> = ({ event, show }) => {
  const [tabs]= useState<ITab[]>([
    { name: "Novice", actions: [] },
    { name: "Open", actions: [] },
    { name: "Excellent", actions: [] },
    { name: "Master", actions: [] },
    { name: "Premier", actions: [] },
  ]);

  const { activeTab, setTab } = useTabContent();

  const [sctData, setSCTData] = useState<AkcAgilitySctDto>(show.akc_agility_sct ?? {
    Novice: [
      { yds: 200, regular_jump_height: '8', regular_sct: '', preferred_jump_height: '4', preferred_sct: '' },
      { yds: 200, regular_jump_height: '12', regular_sct: '', preferred_jump_height: '8', preferred_sct: '' },
      { yds: 200, regular_jump_height: '16', regular_sct: '', preferred_jump_height: '12', preferred_sct: '' },
      { yds: 200, regular_jump_height: '20/24C', regular_sct: '', preferred_jump_height: '16', preferred_sct: '' },
      { yds: 200, regular_jump_height: '24', regular_sct: '', preferred_jump_height: '20', preferred_sct: '' }
    ],
    Open: [
      { yds: 200, regular_jump_height: '8', regular_sct: '', preferred_jump_height: '4', preferred_sct: '' },
      { yds: 200, regular_jump_height: '12', regular_sct: '', preferred_jump_height: '8', preferred_sct: '' },
      { yds: 200, regular_jump_height: '16', regular_sct: '', preferred_jump_height: '12', preferred_sct: '' },
      { yds: 200, regular_jump_height: '20/24C', regular_sct: '', preferred_jump_height: '16', preferred_sct: '' },
      { yds: 200, regular_jump_height: '24', regular_sct: '', preferred_jump_height: '20', preferred_sct: '' }
    ],
    Excellent: [
      { yds: 200, regular_jump_height: '8', regular_sct: '', preferred_jump_height: '4', preferred_sct: '' },
      { yds: 200, regular_jump_height: '12', regular_sct: '', preferred_jump_height: '8', preferred_sct: '' },
      { yds: 200, regular_jump_height: '16', regular_sct: '', preferred_jump_height: '12', preferred_sct: '' },
      { yds: 200, regular_jump_height: '20/24C', regular_sct: '', preferred_jump_height: '16', preferred_sct: '' },
      { yds: 200, regular_jump_height: '24', regular_sct: '', preferred_jump_height: '20', preferred_sct: '' }
    ],
    Master: [
      { yds: 200, regular_jump_height: '8', regular_sct: '', preferred_jump_height: '4', preferred_sct: '' },
      { yds: 200, regular_jump_height: '12', regular_sct: '', preferred_jump_height: '8', preferred_sct: '' },
      { yds: 200, regular_jump_height: '16', regular_sct: '', preferred_jump_height: '12', preferred_sct: '' },
      { yds: 200, regular_jump_height: '20/24C', regular_sct: '', preferred_jump_height: '16', preferred_sct: '' },
      { yds: 200, regular_jump_height: '24', regular_sct: '', preferred_jump_height: '20', preferred_sct: '' }
    ],
    Premier: [
      { yds: 200, regular_jump_height: '8', regular_sct: '', preferred_jump_height: '4', preferred_sct: '' },
      { yds: 200, regular_jump_height: '12', regular_sct: '', preferred_jump_height: '8', preferred_sct: '' },
      { yds: 200, regular_jump_height: '16', regular_sct: '', preferred_jump_height: '12', preferred_sct: '' },
      { yds: 200, regular_jump_height: '20/24C', regular_sct: '', preferred_jump_height: '16', preferred_sct: '' },
      { yds: 200, regular_jump_height: '24', regular_sct: '', preferred_jump_height: '20', preferred_sct: '' }
    ],
  });

  const activeTabName = tabs[activeTab].name;

  const handleInputChange = (tab: string, rowIndex: number, field: keyof AkcAgilitySctEntryDto, value: string): void => {
    setSCTData(prevData => ({
      ...prevData,
      [tab]: prevData[tab].map((row, index) =>
        index === rowIndex
          ? { ...row, [field]: value }
          : row
      )
    }));
  };

  const handleRegularValueChange = (rowIndex: number, value: string): void => {
    handleInputChange(activeTabName, rowIndex, "regular_sct", value);
  };

  const handlePreferredValueChange = (rowIndex: number, value: string): void => {
    handleInputChange(activeTabName, rowIndex, "preferred_sct", value);
  };

  const handleSubmit = () => {
    const updateData: ShowDto = { ...show };

    updateData.akc_agility_sct = sctData;

    apiService.updateShow(event._id, show._id, updateData)
      .then(() => {
        toast.show({
          title: "Update AKC Agility SCT Settings",
          content: "AKC Agility SCT Settings have been successfully updated",
          duration: 5000,
          type: "success"
        });
      })
      .catch(error => {
        toast.show({
          title: "Update AKC Agility SCT Settings",
          content: "Failed to update AKC Agility SCT Settings",
          duration: 5000,
          type: "fail"
        });
      });
  }

  return (
    <div className={styles.AKCAgilitySCTTab}>
      <div className={styles.tabsContent}>
        {!!tabs?.length &&
          <TabContent className="ManageAKCAgilitySCT" setTab={setTab} activeTab={activeTab} tabs={tabs}>
            <SctTable
              key={activeTabName}
              activeTabName={activeTabName}
              sctData={sctData[activeTabName]}
              onRegularValueChange={handleRegularValueChange}
              onPreferredValueChange={handlePreferredValueChange}
            />
          </TabContent>}
      </div>
      <div className={styles.actions}>
        <Button isLoading={false} onClick={handleSubmit}>Save</Button>
      </div>
    </div>
  )
}

export { AKCAgilitySCTTab }
